<template>
    <div class="flex flex-col">
      <div class="overflow-auto flex-1 StepsView scroll-smooth" ref="stepWindow">
        <template v-if="isLoading">
          <div class="w-full h-full flex items-center justify-center">
            <div class="">
              <ActivityIndicator />
            </div>
          </div>
        </template>
        <template v-else>
          <div>
            <!-- //v Chart -->
            <div class="w-full relative chart-container">
              <div
                class="lg:sticky"
                v-if="
                  computedChartData.datasets &&
                  computedChartData.datasets[0] &&
                  computedChartData.datasets[0].data &&
                  computedChartData.datasets[0].data.reduce((a, b) => a + b, 0) !=
                  0
                "
              >
                <Pie
                  class="chart-content m-auto"
                  v-if="forceRerenderChart"
                  :data="updatedChartData"
                  :options="chartOptions"
                />
              </div>
              <div
                class="mt-4 lg:sticky"
                :class="{
                  'opacity-0':
                    !(computedChartData.datasets &&
                    computedChartData.datasets[0] &&
                    computedChartData.datasets[0].data) ||
                    computedChartData.datasets[0].data.reduce((a, b) => a + b, 0) == 0,
                }"
              >
                <div class="content-container flex justify-center">
                  <div>
                    <div
                      class="font-epilogue text-primary-400 text-center"
                      v-if="philanthropicBudgetTotal > 0"
                    >
                      <div class="square-container">
                        <div
                          class="square mr-1"
                          :style="{
                            backgroundColor: getBackgroundColor(1),
                          }"
                        ></div>
                        <p
                          :style="{
                            color: selectedBodyTextColor,
                            fontFamily: selectedFontFamily,
                          }"
                        >
                          Philanthropic :
                          {{ formatToDecimal(philanthropicBudgetTotal - 0) }}
                        </p>
                      </div>
                    </div>
                    <div class="square-container" v-if="earnedBudgetTotal > 0">
                      <p
                        class="square mr-1"
                        :style="{
                          backgroundColor: getBackgroundColor(2),
                        }"
                      ></p>
                      <p
                        :style="{
                          color: selectedBodyTextColor,
                          fontFamily: selectedFontFamily,
                        }"
                      >
                        Earned :
                        {{ formatToDecimal(earnedBudgetTotal - 0) }}
                      </p>
                    </div>
                    <div
                      class="square-container"
                      v-if="investmentBudgetTotal > 0"
                    >
                      <p
                        class="square mr-1"
                        :style="{
                          backgroundColor: getBackgroundColor(3),
                        }"
                      ></p>
                      <p
                        :style="{
                          color: selectedBodyTextColor,
                          fontFamily: selectedFontFamily,
                        }"
                      >
                        Investment :
                        {{ formatToDecimal(investmentBudgetTotal - 0) }}
                      </p>
                    </div>
                    <div
                      class="square-container"
                      v-if="governmentAppropriationsBudgetTotal > 0"
                    >
                      <p
                        class="square mr-1"
                        :style="{
                          backgroundColor: getBackgroundColor(4),
                        }"
                      ></p>
                      <p
                        :style="{
                          color: selectedBodyTextColor,
                          fontFamily: selectedFontFamily,
                        }"
                      >
                        Government :
                        {{
                          formatToDecimal(governmentAppropriationsBudgetTotal - 0)
                        }}
                      </p>
                    </div>
                    <div class="square-container" v-if="inKindBudgetTotal > 0">
                      <p
                        class="square mr-1"
                        :style="{
                          backgroundColor: getBackgroundColor(5),
                        }"
                      ></p>
                      <p
                        :style="{
                          color: selectedBodyTextColor,
                          fontFamily: selectedFontFamily,
                        }"
                      >
                        In-Kind Goods/Services:
                        {{ formatToDecimal(inKindBudgetTotal - 0) }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="w-full flex justify-center font-medium">
                  <p
                    class="text-xl mt-3"
                    :style="{
                      color: selectedBodyTextColor,
                      fontFamily: selectedFontFamily,
                      lineHeight: '15px',
                    }"
                  >
                    Total Revenue:
                    <span class="">{{
                      formatToDecimal(
                        +philanthropicBudgetTotal +
                          +earnedBudgetTotal +
                          +investmentBudgetTotal +
                          +governmentAppropriationsBudgetTotal +
                          +inKindBudgetTotal -
                          0
                      )
                    }}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </template>
  
  <script setup>
  //v Vue Imports
  import { ref, onMounted, reactive, computed, watch } from "vue";
  
  //v Vue Router
  import { RouterLink, RouterView, useRouter, useRoute } from "vue-router";
  const router = useRouter();
  
  // v Chart js
  import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
  import { Pie } from "vue-chartjs";
  
  import chroma from "chroma-js";
  
  ChartJS.register(ArcElement, Tooltip, Legend);
  
  //v Components
  
  import ActivityIndicator from "@/components/ActivityIndicator.vue";
  import formatToDecimal from "../../composables/formatToDecimal";
  
  //v Global
  import global from "@/global";
  
  //v Pinia Store: Auth
  import { useAuthStore } from "@/stores/auth";
  const authStore = useAuthStore();
  import { useRoutesManagement } from "@/stores/routesManagement";
  const routesManagementStore = useRoutesManagement();
  
  //v assets
  import characterImage from "@/assets/character-image.png";
  
  const props = defineProps({
    selectedHeadingsColor: String,
    selectedFontFamily: String,
    selectedBodyTextColor: String,
    revenuesData: Object, // Add this new prop
  });
  
  const stepWindow = ref(null);
  const philanthropicPointListContainer = ref(null);
  const earnedPointListContainer = ref(null);
  const investmentPointListContainer = ref(null);
  const inKindPointListContainer = ref(null);
  const isLoading = ref(null);
  
  let nearestColors = reactive([]);
  
  const findNearestColors = () => {
    const colorValue = props.selectedHeadingsColor || '#00423D';
    const targetChroma = chroma(colorValue);
    const hue = targetChroma.get("hsl.h");
    const steps = 8;
    const stepSize = 100 / (steps - 1);
    let nearestColorsTmp = [];
    for (let i = 0; i < steps; i++) {
      const lightness = i * stepSize;
      nearestColorsTmp.push(`hsl(${hue}, 100%, ${lightness}%)`);
    }
    nearestColors = nearestColorsTmp;
  };
  findNearestColors();
  
  function getBackgroundColor(index) {
    return nearestColors[index];
  }
  
  //v Chart State
  const chartState = reactive({
    chartData: {
      labels: [
        "Philanthropic Contributions",
        "Earned Revenue",
        "Investment Income",
        "Government Appropriations",
        "In-Kind Goods/Services",
      ],
      datasets: [
        {
          backgroundColor: [],
          borderWidth: 0,
          data: [0, 0, 0, 0, 0],
        },
      ],
    },
  });
  
  const computedChartData = computed(() => {
    return chartState.chartData;
  });
  
  const updatedChartData = computed(() => {
    findNearestColors();
    chartState.chartData.datasets[0].backgroundColor = [
      nearestColors[1],
      nearestColors[2],
      nearestColors[3],
      nearestColors[4],
      nearestColors[5],
    ];
    return chartState.chartData;
  });
  
  const chartOptions = ref({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.8)",
        titleColor: "#0D423D",
        titleFont: {
          weight: "normal",
          size: 15,
        },
        padding: 10,
        borderColor: "#0D423D",
        borderWidth: 1,
        titleAlign: "center",
        bodyColor: "#0D423D",
        bodyAlign: "center",
        bodyFont: {
          weight: "bold",
          size: 14,
        },
        usePointStyle: true,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
  
            if (label) {
              label += ": ";
            }
  
            if (context.parsed !== null) {
              label += context.parsed.toLocaleString();
            }
            return label;
          },
          labelPointStyle: function (context) {
            return {
              pointStyle: "circle",
              rotation: 0,
            };
          },
        },
      },
    },
  });
  
  const forceRerenderChart = ref(true);
  //v Total rev
  const totalRevenue = ref(0);
  
  //v Handling Philanthropic Budget Expenses
  const philanthropicBudgetTotal = ref(0);
  const isPhilanthropicBudgetValid = ref({ isValid: true, issue: "none" });
  const philanthropicBudgetPointList = ref([
    {
      id: -1,
      pointText: "Corporate Gifts",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Individual Gifts",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Foundation Grants",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Government Grants",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Corporate Sponsorships",
      pointValue: "",
    },
  ]);
  
  function removeCommas(numberString) {
    return numberString.replace(/,/g, "");
  }
  
  function formatNumberWithCommas(number) {
    return number.toLocaleString("en-US");
  }
  
  function onPhilanthropicBudgetChange(budgetList) {
    philanthropicBudgetPointList.value = budgetList;
  
    if (budgetList.length !== 0) {
      let total = 0;
      budgetList.forEach((point) => {
        const numericValue = parseFloat(removeCommas(point.pointValue)); // Remove commas
        total += isNaN(numericValue) ? 0 : numericValue;
      });
  
      philanthropicBudgetTotal.value =
        total === 0 ? "00.00" : formatNumberWithCommas(total.toFixed(2));
    } else {
      philanthropicBudgetTotal.value = "00.00";
    }
  
    if (
      chartState.chartData &&
      Array.isArray(chartState.chartData.datasets) &&
      chartState.chartData.datasets.length > 0 &&
      Array.isArray(chartState.chartData.datasets[0].data)
    ) {
      chartState.chartData.datasets[0].data[0] = parseFloat(
        philanthropicBudgetTotal.value
      );
    }
  }
  
  function checkPhilanthropicBudgetValidation() {
    if (philanthropicBudgetPointList.value.length === 0) {
      isPhilanthropicBudgetValid.value = { isValid: false, issue: "empty" };
      return;
    }
  
    for (let i = 0; i < philanthropicBudgetPointList.value.length; i++) {
      const point = philanthropicBudgetPointList.value[i];
      if (!point.pointText || !point.pointValue) {
        isPhilanthropicBudgetValid.value = {
          isValid: false,
          issue: "incomplete",
        };
        return;
      }
    }
    isPhilanthropicBudgetValid.value = { isValid: true, issue: "none" };
  }
  
  watch(
    philanthropicBudgetPointList,
    (newObj) => {
      if (!isPhilanthropicBudgetValid.value.isValid) {
        isPhilanthropicBudgetValid.value = { isValid: true, issue: "none" };
      }
    },
    { deep: true }
  );
  
  //v Handling Earned Budget Expenses
  const earnedBudgetTotal = ref(0);
  const isEarnedBudgetValid = ref({ isValid: true, issue: "none" });
  const earnedBudgetPointList = ref([
    {
      id: -1,
      pointText: "Ticket Sales",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Events",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Licensing Fees",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Rentals",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Tuition",
      pointValue: "",
    },
  ]);
  
  function onEarnedBudgetChange(budgetList) {
    earnedBudgetPointList.value = budgetList;
    //v calculate total
    if (budgetList.length !== 0) {
      let total = 0;
      budgetList.forEach((point) => {
        const numericValue = parseFloat(removeCommas(point.pointValue)); // Remove commas
        total += isNaN(numericValue) ? 0 : numericValue;
      });
  
      earnedBudgetTotal.value =
        total === 0 ? "00.00" : formatNumberWithCommas(total.toFixed(2));
    } else {
      earnedBudgetTotal.value = "00.00";
    }
  
    if (
      chartState.chartData &&
      Array.isArray(chartState.chartData.datasets) &&
      chartState.chartData.datasets.length > 0 &&
      Array.isArray(chartState.chartData.datasets[0].data)
    ) {
      chartState.chartData.datasets[0].data[1] = parseFloat(
        earnedBudgetTotal.value
      );
    }
  }
  
  function checkEarnedBudgetValidation() {
    if (earnedBudgetPointList.value.length === 0) {
      isEarnedBudgetValid.value = { isValid: false, issue: "empty" };
      return;
    }
  
    for (let i = 0; i < earnedBudgetPointList.value.length; i++) {
      const point = earnedBudgetPointList.value[i];
      if (!point.pointText || !point.pointValue) {
        isEarnedBudgetValid.value = {
          isValid: false,
          issue: "incomplete",
        };
        return;
      }
    }
    isEarnedBudgetValid.value = { isValid: true, issue: "none" };
  }
  
  watch(
    earnedBudgetPointList,
    (newObj) => {
      if (!isEarnedBudgetValid.value.isValid) {
        isEarnedBudgetValid.value = { isValid: true, issue: "none" };
      }
    },
    { deep: true }
  );
  
  //v Handling Investment Budget Expenses
  const investmentBudgetTotal = ref(0);
  const isInvestmentBudgetValid = ref({ isValid: true, issue: "none" });
  const investmentBudgetPointList = ref([
    {
      id: -1,
      pointText: "Endowment Income",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Stock/Bonds Dividends",
      pointValue: "",
    },
  ]);
  
  function onInvestmentBudgetChange(budgetList) {
    investmentBudgetPointList.value = budgetList;
    //v calculate total
  
    if (budgetList.length !== 0) {
      let total = 0;
      budgetList.forEach((point) => {
        const numericValue = parseFloat(removeCommas(point.pointValue)); // Remove commas
        total += isNaN(numericValue) ? 0 : numericValue;
      });
  
      investmentBudgetTotal.value =
        total === 0 ? "00.00" : formatNumberWithCommas(total.toFixed(2));
    } else {
      investmentBudgetTotal.value = "00.00";
    }
  
    if (
      chartState.chartData &&
      Array.isArray(chartState.chartData.datasets) &&
      chartState.chartData.datasets.length > 0 &&
      Array.isArray(chartState.chartData.datasets[0].data)
    ) {
      chartState.chartData.datasets[0].data[2] = parseFloat(
        investmentBudgetTotal.value
      );
    }
  }
  
  function checkInvestmentBudgetValidation() {
    if (investmentBudgetPointList.value.length === 0) {
      isInvestmentBudgetValid.value = { isValid: false, issue: "empty" };
      return;
    }
  
    for (let i = 0; i < investmentBudgetPointList.value.length; i++) {
      const point = investmentBudgetPointList.value[i];
      if (!point.pointText || !point.pointValue) {
        isInvestmentBudgetValid.value = {
          isValid: false,
          issue: "incomplete",
        };
        return;
      }
    }
    isInvestmentBudgetValid.value = { isValid: true, issue: "none" };
  }
  
  watch(
    investmentBudgetPointList,
    (newObj) => {
      if (!isInvestmentBudgetValid.value.isValid) {
        isInvestmentBudgetValid.value = { isValid: true, issue: "none" };
      }
    },
    { deep: true }
  );
  
  //v Handling Government Appropriations Expenses
  const governmentAppropriationsBudgetTotal = ref(0);
  const governmentAppropriationsBudgetValid = ref({
    isValid: true,
    issue: "none",
  });
  const governmentAppropriationsBudgetPointList = ref([
    {
      id: -1,
      pointText: "City",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "State",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Federal",
      pointValue: "",
    },
  ]);
  
  function governmentAppropriationsBudgetChange(budgetList) {
    governmentAppropriationsBudgetPointList.value = budgetList;
    //v calculate total
    if (budgetList.length !== 0) {
      let total = 0;
      budgetList.forEach((point) => {
        const numericValue = parseFloat(removeCommas(point.pointValue)); // Remove commas
        total += isNaN(numericValue) ? 0 : numericValue;
      });
  
      governmentAppropriationsBudgetTotal.value =
        total === 0 ? "00.00" : formatNumberWithCommas(total.toFixed(2));
    } else {
      governmentAppropriationsBudgetTotal.value = "00.00";
    }
  
    if (
      chartState.chartData &&
      Array.isArray(chartState.chartData.datasets) &&
      chartState.chartData.datasets.length > 0 &&
      Array.isArray(chartState.chartData.datasets[0].data)
    ) {
      chartState.chartData.datasets[0].data[3] = parseFloat(
        governmentAppropriationsBudgetTotal.value
      );
    }
  }
  
  function checkGovernmentAppropriationsBudgetValidation() {
    if (governmentAppropriationsBudgetPointList.value.length === 0) {
      governmentAppropriationsBudgetValid.value = {
        isValid: false,
        issue: "empty",
      };
      return;
    }
  
    for (let i = 0; i < investmentBudgetPointList.value.length; i++) {
      const point = investmentBudgetPointList.value[i];
      if (!point.pointText || !point.pointValue) {
        governmentAppropriationsBudgetValid.value = {
          isValid: false,
          issue: "incomplete",
        };
        return;
      }
    }
    governmentAppropriationsBudgetValid.value = { isValid: true, issue: "none" };
  }
  
  watch(
    investmentBudgetPointList,
    (newObj) => {
      if (!governmentAppropriationsBudgetValid.value.isValid) {
        governmentAppropriationsBudgetValid.value = {
          isValid: true,
          issue: "none",
        };
      }
    },
    { deep: true }
  );
  
  //v Handling In Kind Budget Expenses
  const inKindBudgetTotal = ref(0);
  const isInKindBudgetValid = ref({ isValid: true, issue: "none" });
  const inKindBudgetPointList = ref([
    {
      id: -1,
      pointText: "Salaries & Benefits",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Administrative Expenses",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Supplies & Materials",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Travel & Transportation",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Professional Development",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Outreach & Marketing",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Program Evaluation & Assessment",
      pointValue: "",
    },
    {
      id: -1,
      pointText: "Miscellaneous Expenses",
      pointValue: "",
    },
  ]);
  
  function onInKindBudgetChange(budgetList) {
    inKindBudgetPointList.value = budgetList;
  
    if (budgetList.length !== 0) {
      let total = 0;
      budgetList.forEach((point) => {
        const numericValue = parseFloat(removeCommas(point.pointValue));
        total += isNaN(numericValue) ? 0 : numericValue;
      });
  
      inKindBudgetTotal.value =
        total === 0 ? "00.00" : formatNumberWithCommas(total.toFixed(2));
    } else {
      inKindBudgetTotal.value = "00.00";
    }
  
    if (
      chartState.chartData &&
      Array.isArray(chartState.chartData.datasets) &&
      chartState.chartData.datasets.length > 0 &&
      Array.isArray(chartState.chartData.datasets[0].data)
    ) {
      chartState.chartData.datasets[0].data[4] = parseFloat(
        inKindBudgetTotal.value
      );
    }
  }
  
  function checkInKindBudgetValidation() {
    if (inKindBudgetPointList.value.length === 0) {
      isInKindBudgetValid.value = { isValid: false, issue: "empty" };
      return;
    }
  
    for (let i = 0; i < inKindBudgetPointList.value.length; i++) {
      const point = inKindBudgetPointList.value[i];
      if (!point.pointText || !point.pointValue) {
        isInKindBudgetValid.value = {
          isValid: false,
          issue: "incomplete",
        };
        return;
      }
    }
    isInKindBudgetValid.value = { isValid: true, issue: "none" };
  }
  
  watch(
    inKindBudgetPointList,
    (newObj) => {
      if (!isInKindBudgetValid.value.isValid) {
        isInKindBudgetValid.value = { isValid: true, issue: "none" };
      }
    },
    { deep: true }
  );
  
  //v watch chart state and rerender chart on chart data change
  watch(
    chartState,
    (newData, oldData) => {
      forceRerenderChart.value = false;
      setTimeout(() => {
        forceRerenderChart.value = true;
      }, 1);
    },
    { immediate: true }
  );
  
  //v request/response actions
  // error, fetchCachedData, cachedData

  const retrievedUserAnswer = ref(null);
  
  onMounted(async () => {
    try {
      // Check if we have revenues data from props
      if (props.revenuesData) {
        const tempRes = props.revenuesData;
        
        // Philanthropic Contributions
        if (tempRes.philanthropic_contributions !== undefined) {
          philanthropicBudgetTotal.value = parseFloat(tempRes.philanthropic_contributions || 0);
          chartState.chartData.datasets[0].data[0] = philanthropicBudgetTotal.value;
        }

        // Earned Revenue
        if (tempRes.earned_revenue !== undefined) {
          earnedBudgetTotal.value = parseFloat(tempRes.earned_revenue || 0);
          chartState.chartData.datasets[0].data[1] = earnedBudgetTotal.value;
        }

        // Investment Income
        if (tempRes.investment_income !== undefined) {
          investmentBudgetTotal.value = parseFloat(tempRes.investment_income || 0);
          chartState.chartData.datasets[0].data[2] = investmentBudgetTotal.value;
        }

        // Government Appropriations
        if (tempRes.government_appropriations !== undefined) {
          governmentAppropriationsBudgetTotal.value = parseFloat(tempRes.government_appropriations || 0);
          chartState.chartData.datasets[0].data[3] = governmentAppropriationsBudgetTotal.value;
        }

        // In-Kind Goods/Services
        if (tempRes.in_kind_goods_and_services !== undefined) {
          inKindBudgetTotal.value = parseFloat(tempRes.in_kind_goods_and_services || 0);
          chartState.chartData.datasets[0].data[4] = inKindBudgetTotal.value;
        }

        // Force chart to update
        forceRerenderChart.value = false;
        setTimeout(() => {
          forceRerenderChart.value = true;
        }, 1);
      } else {
        // Fallback to original API if no props data
        const response = await fetch(`${global.API_URL}/case-statements`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
            "Content-Type": "application/json",
          },
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const data = await response.json();
        const tempRes = data.data?.revenues;
        
        if (tempRes) {
          // Philanthropic Contributions
          if (tempRes.philanthropic_contributions !== undefined) {
            philanthropicBudgetTotal.value = parseFloat(tempRes.philanthropic_contributions || 0);
            chartState.chartData.datasets[0].data[0] = philanthropicBudgetTotal.value;
          }

          // Earned Revenue
          if (tempRes.earned_revenue !== undefined) {
            earnedBudgetTotal.value = parseFloat(tempRes.earned_revenue || 0);
            chartState.chartData.datasets[0].data[1] = earnedBudgetTotal.value;
          }

          // Investment Income
          if (tempRes.investment_income !== undefined) {
            investmentBudgetTotal.value = parseFloat(tempRes.investment_income || 0);
            chartState.chartData.datasets[0].data[2] = investmentBudgetTotal.value;
          }

          // Government Appropriations
          if (tempRes.government_appropriations !== undefined) {
            governmentAppropriationsBudgetTotal.value = parseFloat(tempRes.government_appropriations || 0);
            chartState.chartData.datasets[0].data[3] = governmentAppropriationsBudgetTotal.value;
          }

          // In-Kind Goods/Services
          if (tempRes.in_kind_goods_and_services !== undefined) {
            inKindBudgetTotal.value = parseFloat(tempRes.in_kind_goods_and_services || 0);
            chartState.chartData.datasets[0].data[4] = inKindBudgetTotal.value;
          }

          // Force chart to update
          forceRerenderChart.value = false;
          setTimeout(() => {
            forceRerenderChart.value = true;
          }, 1);
        }
      }
    } catch (error) {
      console.error('Error processing revenues data:', error);
    } finally {
      isLoading.value = false;
    }
  });

  // Add a watcher to update chart when revenuesData changes
  watch(
    () => props.revenuesData,
    (newRevenuesData) => {
      if (newRevenuesData) {
        // Philanthropic Contributions
        if (newRevenuesData.philanthropic_contributions !== undefined) {
          philanthropicBudgetTotal.value = parseFloat(newRevenuesData.philanthropic_contributions || 0);
          chartState.chartData.datasets[0].data[0] = philanthropicBudgetTotal.value;
        }

        // Earned Revenue
        if (newRevenuesData.earned_revenue !== undefined) {
          earnedBudgetTotal.value = parseFloat(newRevenuesData.earned_revenue || 0);
          chartState.chartData.datasets[0].data[1] = earnedBudgetTotal.value;
        }

        // Investment Income
        if (newRevenuesData.investment_income !== undefined) {
          investmentBudgetTotal.value = parseFloat(newRevenuesData.investment_income || 0);
          chartState.chartData.datasets[0].data[2] = investmentBudgetTotal.value;
        }

        // Government Appropriations
        if (newRevenuesData.government_appropriations !== undefined) {
          governmentAppropriationsBudgetTotal.value = parseFloat(newRevenuesData.government_appropriations || 0);
          chartState.chartData.datasets[0].data[3] = governmentAppropriationsBudgetTotal.value;
        }

        // In-Kind Goods/Services
        if (newRevenuesData.in_kind_goods_and_services !== undefined) {
          inKindBudgetTotal.value = parseFloat(newRevenuesData.in_kind_goods_and_services || 0);
          chartState.chartData.datasets[0].data[4] = inKindBudgetTotal.value;
        }

        // Force chart to update
        forceRerenderChart.value = false;
        setTimeout(() => {
          forceRerenderChart.value = true;
        }, 1);
      }
    },
    { immediate: true, deep: true }
  );
  </script>
  
  <style scoped>
  .chart-container {
    margin-top: 8px;
    min-height: 520px;
    max-height: 520px;
    overflow: hidden;
  }
  .chart-content {
    width: 155px !important;
    height: 155px !important;
  }
  .content-container {
    margin-bottom: 10px;
  }
  .square-container .square {
    position: absolute;
    top: 0;
    left: 0;
    width: 10px;
    height: 10px;
    margin-top: 5.9px;
  }
  .square-container {
    position: relative;
    padding-left: 1rem;
  }
  .square-container {
    text-align: left;
  }
  </style>
  